import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BusinessIcon from '@mui/icons-material/Business';
import { Link as RouterLink } from 'react-router-dom';
import { companiesAPI } from '../services/api';

const UploaderMyCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await companiesAPI.getAll({ page: 1, limit: 5000 });
        const data = response.data?.data || response.data?.items || response.data || [];
        setCompanies(data);
      } catch (e) {
        console.error(e);
        setError('Failed to load your assigned companies.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          My Companies
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a company to upload or correct documents.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Skeleton variant="rectangular" height={170} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : companies.length === 0 ? (
        <Alert severity="info">No companies are assigned to your uploader account yet.</Alert>
      ) : (
        <Grid container spacing={2}>
          {companies.map((company) => (
            <Grid item xs={12} sm={6} md={4} key={company.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon color="primary" />
                      <Typography variant="h6">{company.name}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {company.jurisdiction && <Chip size="small" label={company.jurisdiction} />}
                      {company.registrationNumber && (
                        <Chip size="small" variant="outlined" label={company.registrationNumber} />
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
                <CardActions>
                  <Button
                    component={RouterLink}
                    to={`/my-companies/${company.id}`}
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    fullWidth
                  >
                    Open Upload Desk
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default UploaderMyCompanies;
